// ABOUTME: Keeps keyboard focus inside an open overlay: it moves in when the overlay opens, Tab cycles within it,
// ABOUTME: and whatever had focus before gets it back on close — so nothing under the overlay can be reached or pressed.
import { useEffect, type RefObject } from "react"

/** What Tab stops on — and nothing with tabindex="-1", which the browser itself skips. */
const FOCUSABLE =
  'a[href], button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Traps focus in `panelRef` while `isActive`. The panel must be focusable itself (tabIndex -1): with nothing
 * inside claiming focus, it takes it.
 */
export function useFocusTrap(panelRef: RefObject<HTMLElement | null>, isActive: boolean): void {
  useEffect(() => {
    const panel = panelRef.current
    if (!isActive || !panel) return

    const restoreTo = document.activeElement as HTMLElement | null
    const focusable = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
        // `checkVisibility` skips what the browser itself would skip when tabbing;
        // where it is missing (jsdom) everything counts, which is what tests want.
      ).filter((el) => (typeof el.checkVisibility === "function" ? el.checkVisibility() : true))

    // A field with autoFocus has already claimed it — moving focus to the close
    // button would undo exactly what the screen wanted.
    if (!panel.contains(document.activeElement)) panel.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return
      const items = focusable()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      // The panel itself holds focus once open (and after a click on its body) and comes before every item:
      // the browser's Shift+Tab from it would step out to the page under the overlay.
      const isAtStart = document.activeElement === first || document.activeElement === panel
      if (event.shiftKey && isAtStart) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    panel.addEventListener("keydown", onKeyDown)
    return () => {
      panel.removeEventListener("keydown", onKeyDown)
      restoreTo?.focus()
    }
  }, [isActive, panelRef])
}
